import { useTranslation } from 'react-i18next';
import { quantityText, unitPriceText } from '@/lib/lineDisplay';
import { Link } from 'react-router-dom';
import type { components } from '@/api/generated/schema';
import { formatCurrency, formatDate } from '@/lib/format';

type Line = components['schemas']['LineItemResponse'];
type SourceEstimate = components['schemas']['JobSourceEstimateResponse'];

/**
 * The work a job carries from the approved estimates converted onto it
 * (M2-09): where it came from, then the lines, figure for figure. Read-only —
 * the lines are the approved ones, and nothing edits a job's lines yet.
 *
 * Nothing is shown for a job with neither (every job before M2-09, and any
 * job booked by hand), so the page stays as it was for them. Everyone who can
 * open the job sees it, the technician included: the job's own work is theirs
 * to read, and each estimate link opens for them because the estimate's
 * lines are on this job.
 *
 * An estimate drafted on another job (M2-09 follow-up: that job had finished,
 * so the work came here) also links that job — this one is its follow-up.
 */
export function JobWork({
  lines,
  sourceEstimates,
  timeZone,
}: {
  lines: Line[];
  sourceEstimates: SourceEstimate[];
  /** The SHOP's zone, for the conversion dates — the clock every instant on the job page uses. */
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();

  if (lines.length === 0 && sourceEstimates.length === 0) {
    return null;
  }

  const on = (value: string) =>
    formatDate(value, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) });

  return (
    <section aria-labelledby="job-work-heading" className="space-y-3">
      <h2 id="job-work-heading" className="text-base font-semibold">
        {t('jobDetail.work.heading')}
      </h2>

      <ul className="space-y-1 text-sm" data-testid="job-source-estimates">
        {sourceEstimates.map((e) => {
          const number = Number(e.number);
          const revision = Number(e.revision);
          return (
            <li key={e.id}>
              <Link to={`/estimates/${e.id}`} className="font-medium hover:underline">
                {revision > 1
                  ? t('jobDetail.work.fromEstimateRevision', { number, revision })
                  : t('jobDetail.work.fromEstimate', { number })}
              </Link>{' '}
              <span className="text-[var(--color-muted-foreground)]">
                {t('jobDetail.work.convertedOn', { date: on(e.convertedAt) })}
              </span>
              {/* M2-09 follow-up: this job is the follow-up to the visit the
                  estimate was drafted on, which had finished. */}
              {e.draftedOnJobId ? (
                <>
                  {' · '}
                  <Link to={`/jobs/${e.draftedOnJobId}`} className="hover:underline">
                    {t('jobDetail.work.draftedOn')}
                  </Link>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>

      {lines.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[var(--color-muted-foreground)]">
              <tr>
                <th scope="col" className="py-2 pr-3 font-medium">
                  {t('jobDetail.work.columns.description')}
                </th>
                <th scope="col" className="py-2 pr-3 text-right font-medium">
                  {t('jobDetail.work.columns.quantity')}
                </th>
                <th scope="col" className="hidden py-2 pr-3 text-right font-medium sm:table-cell">
                  {t('jobDetail.work.columns.unitPrice')}
                </th>
                <th scope="col" className="py-2 text-right font-medium">
                  {t('jobDetail.work.columns.total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr
                  key={line.id}
                  className="border-t border-[var(--color-border)]"
                  data-testid="job-line"
                >
                  <td className="py-2 pr-3">{line.description}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{quantityText(line, t)}</td>
                  <td className="hidden py-2 pr-3 text-right tabular-nums sm:table-cell">
                    {unitPriceText(line, t)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatCurrency(Number(line.total))}
                    {Number(line.discount) > 0 ? (
                      <span className="block text-xs text-[var(--color-muted-foreground)]">
                        {t('jobDetail.work.discount', {
                          amount: formatCurrency(Number(line.discount)),
                        })}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
